const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

export const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === '') return 0;
  const raw = String(value).trim().replace(/\s/g, '');
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatCurrency = (value) => toNumber(value).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

export const formatDecimal = (value, fractionDigits = 2) => toNumber(value).toLocaleString('pt-BR', {
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits,
});

export const formatDocument = (value) => {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return raw;
};

const joinAddress = (...parts) => parts
  .map((part) => String(part || '').trim())
  .filter(Boolean)
  .join(', ');

const buildDetailedAddress = (source, prefix) => {
  if (!source) return '';
  const street = source[`${prefix}_logradouro`];
  const number = source[`${prefix}_numero`];
  const complement = source[`${prefix}_complemento`];
  const district = source[`${prefix}_bairro`];
  const city = source[`${prefix}_cidade`];
  const uf = source[`${prefix}_uf`];
  const cep = source[`${prefix}_cep`];
  const condominium = source[`${prefix}_condominio`];
  const cityUf = [city, uf].filter(Boolean).join('/');
  return joinAddress(
    joinAddress(street, number),
    complement,
    district,
    cityUf,
    cep ? `CEP ${cep}` : '',
    condominium,
  );
};

const underThousandToWords = (number) => {
  const value = Math.floor(number);
  if (value === 0) return '';
  if (value === 100) return 'cem';
  const parts = [];
  const hundred = Math.floor(value / 100);
  const rest = value % 100;
  if (hundred) parts.push(centenas[hundred]);
  if (rest) {
    if (parts.length) parts.push('e');
    if (rest < 10) parts.push(unidades[rest]);
    else if (rest < 20) parts.push(especiais[rest - 10]);
    else {
      const ten = Math.floor(rest / 10);
      const unit = rest % 10;
      parts.push(dezenas[ten]);
      if (unit) parts.push('e', unidades[unit]);
    }
  }
  return parts.join(' ');
};

const integerToWords = (number) => {
  const value = Math.floor(Math.abs(number));
  if (value === 0) return 'zero';
  const scales = [
    { value: 1_000_000_000, singular: 'bilhão', plural: 'bilhões' },
    { value: 1_000_000, singular: 'milhão', plural: 'milhões' },
    { value: 1_000, singular: 'mil', plural: 'mil' },
  ];
  let remaining = value;
  const parts = [];
  scales.forEach((scale) => {
    const amount = Math.floor(remaining / scale.value);
    if (!amount) return;
    if (scale.value === 1_000 && amount === 1) parts.push('mil');
    else parts.push(`${underThousandToWords(amount)} ${amount === 1 ? scale.singular : scale.plural}`);
    remaining %= scale.value;
  });
  if (remaining) parts.push(underThousandToWords(remaining));
  return parts.join(' e ');
};

export const currencyToWords = (value) => {
  const amount = Math.round(toNumber(value) * 100);
  const reais = Math.floor(amount / 100);
  const centavos = amount % 100;
  const reaisText = `${integerToWords(reais)} ${reais === 1 ? 'real' : 'reais'}`;
  const centsText = centavos
    ? ` e ${integerToWords(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`
    : '';
  const text = `${reaisText}${centsText}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatDateShort = (dateValue) => {
  if (!dateValue) return 'Não informado';
  const [year, month, day] = String(dateValue).slice(0, 10).split('-');
  if (!year || !month || !day) return String(dateValue);
  return `${day}/${month}/${year}`;
};

export const formatDateLong = (dateValue) => {
  if (!dateValue) return 'DATA NÃO INFORMADA';
  const [year, month, day] = String(dateValue).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return String(dateValue).toUpperCase();
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day))).toUpperCase();
};

const firstParticipantByRole = (participants, roles) => {
  const candidates = (participants || []).filter((item) => roles.includes(item.role));
  return candidates.find((item) => item.is_primary) || candidates[0] || null;
};

const valueOr = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '';

export const buildReportViewModel = (rawData) => {
  const card = rawData?.card || {};
  const production = rawData?.producao || {};
  const order = rawData?.pedido || {};
  const participants = rawData?.participantes || [];
  const client = firstParticipantByRole(participants, ['cliente', 'empresa']);
  const partner = firstParticipantByRole(participants, ['parceiro']);

  const externalBase = toNumber(order.valor_externas_esquadrias);
  const externalDiscount = toNumber(order.desconto_externas);
  const calculatedExternal = externalBase - (externalBase * externalDiscount / 100);
  const hasFinalExternal = order.valor_final_externas !== undefined
    && order.valor_final_externas !== null
    && String(order.valor_final_externas).trim() !== '';
  const finalExternal = hasFinalExternal ? toNumber(order.valor_final_externas) : calculatedExternal;
  const otherBase = toNumber(order.valor_outros_esquadrias);
  const otherDiscount = toNumber(order.desconto_outros);
  const calculatedOther = otherBase - (otherBase * otherDiscount / 100);
  const hasFinalOther = order.valor_final_outros !== undefined
    && order.valor_final_outros !== null
    && String(order.valor_final_outros).trim() !== '';
  const finalOther = hasFinalOther ? toNumber(order.valor_final_outros) : calculatedOther;
  const productsValue = finalExternal + finalOther;
  const bazzeServicesValue = toNumber(order.valor_instalacao)
    + toNumber(order.valor_frete)
    + toNumber(order.valor_abatimento_showroom);
  const thirdPartyValue = toNumber(order.valor_instalacao_pvc)
    + toNumber(order.valor_frete_esquadrias)
    + toNumber(order.valor_projeto)
    + toNumber(order.valor_vidros_separados);
  const calculatedBazzeValue = productsValue + bazzeServicesValue;
  const calculatedTotal = calculatedBazzeValue + thirdPartyValue;
  const fallbackTotal = toNumber(card.sale_value);
  const totalValue = calculatedTotal || fallbackTotal;
  const bazzeValue = calculatedBazzeValue || Math.max(totalValue - thirdPartyValue, 0);

  const billingAddress = valueOr(
    buildDetailedAddress(order, 'endereco_cobranca'),
    buildDetailedAddress(client, 'endereco_cobranca'),
    client?.endereco,
    card.endereco,
  );
  const deliveryAddress = valueOr(
    buildDetailedAddress(order, 'endereco_entrega'),
    buildDetailedAddress(client, 'endereco_entrega'),
    order.cno ? 'Conforme CNO da obra' : '',
    card.endereco,
  );

  return {
    cardId: card.card_id,
    product: card.produto || '',
    proposalCode: valueOr(card.second_document_number, card.document_number),
    prefSuite: card.second_document_number || '',
    faktory: card.pedido_number || '',
    representative: valueOr(rawData?.representante_pedido, card.responsavel),
    clientName: valueOr(order.nome_cliente, client?.name, card.name),
    clientDocument: formatDocument(valueOr(order.cpf_cnpj, client?.cpf, card.cpf)),
    clientPhone: valueOr(order.telefone_cliente, client?.telefone, card.fone),
    clientEmail: valueOr(order.email_nota_fiscal, client?.email, card.email),
    clientAddress: billingAddress,
    contactName: valueOr(order.nome_contato, client?.nome_contato),
    legalRepresentativeName: valueOr(order.representante_legal_nome, client?.representante_legal_nome),
    legalRepresentativeDocument: formatDocument(valueOr(order.representante_legal_cpf, client?.representante_legal_cpf)),
    legalRepresentativeEmail: valueOr(order.representante_legal_email, client?.representante_legal_email),
    workName: valueOr(order.nome_obra, production.nome_obra),
    cno: order.cno || '',
    deliveryAddress,
    wallAreaM2: toNumber(production.metros_quadrados),
    concreteVolumeM3: toNumber(production.volume_concreto_m3),
    loadVolumeM3: toNumber(production.volume_carga_m3),
    loadWeightKg: toNumber(production.peso_carga_kg),
    paymentTerms: order.condicoes_pagamento || '',
    thirdPartyPaymentTerms: order.condicoes_pagamento_terceiro || '',
    bazzeValue,
    thirdPartyValue,
    totalValue,
    activeOrderVersion: order.versao || null,
    partner,
  };
};

const required = (label, value, numeric = false) => {
  const missing = numeric ? toNumber(value) <= 0 : !String(value || '').trim();
  return missing ? label : null;
};

export const getMissingFields = (reportType, data) => {
  const common = [
    required('PrefSuite / código da proposta', data.proposalCode),
    required('nome do cliente', data.clientName),
    required('CPF/CNPJ do cliente', data.clientDocument),
    required('nome da obra', data.workName),
    required('valor total', data.totalValue, true),
    required('condições de pagamento', data.paymentTerms),
  ];
  const specific = reportType === 'contract'
    ? [
      required('endereço do contratante', data.clientAddress),
      required('representante legal', data.legalRepresentativeName),
      required('CPF do representante legal', data.legalRepresentativeDocument),
      required('volume da carga', data.loadVolumeM3, true),
      required('peso da carga', data.loadWeightKg, true),
    ]
    : [
      required('área de parede', data.wallAreaM2, true),
      required('volume de concreto', data.concreteVolumeM3, true),
    ];
  return [...common, ...specific].filter(Boolean);
};
